import { ref } from "vue";
import { defineStore } from "pinia";

export interface SidebarSection {
    name: string;
    open: boolean;
}

const STORAGE_KEY = 'sidebar_sections_state';

const DEFAULT_SECTIONS: SidebarSection[] = [
    { name: 'site_admin', open: false },
    { name: 'transport_site_configuration', open: false },
    { name: 'warehousing_site_configuration', open: false },
    { name: 'site_configuration', open: false },
    { name: 'announcements', open: false },
    { name: 'krakend', open: false },
    { name: 'client_documentation', open: false },
    { name: 'applications_api', open: false },
    { name: 'order_entry', open: false },
    { name: 'etransport', open: false },
    { name: 'klg_users', open: true },
    { name: 'clients', open: true },
    { name: 'erp', open: true },
    { name: 'business_lines', open: true },
    { name: 'ai', open: true },
];

export const useSidebarSectionsStore = defineStore("sidebarSections", () => {
    const sections = ref<SidebarSection[]>([]);

    const loadSectionsFromStorage = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsedSections = JSON.parse(stored) as any[];

                const migratedSections = parsedSections.map(section => {
                    if ('name' in section && 'open' in section) {
                        return {
                            name: section.name,
                            open: section.open
                        };
                    }
                    return section;
                });

                const existingNames = migratedSections.map((s: any) => s.name);
                const missingDefaults = DEFAULT_SECTIONS.filter(
                    def => !existingNames.includes(def.name)
                );

                sections.value = [...migratedSections, ...missingDefaults] as SidebarSection[];
                saveSectionsToStorage();
            } else {
                sections.value = [...DEFAULT_SECTIONS];
                saveSectionsToStorage();
            }
        } catch (error) {
            console.error('Error loading sidebar sections from localStorage:', error);
            sections.value = [...DEFAULT_SECTIONS];
        }
    };

    const saveSectionsToStorage = () => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(sections.value));
        } catch (error) {
            console.error('Error saving sidebar sections to localStorage:', error);
        }
    };

    const toggleSection = (name: string) => {
        const section = sections.value.find(s => s.name === name);
        if (section) {
            section.open = !section.open;
            saveSectionsToStorage();
        }
    };

    const setSectionState = (name: string, open: boolean) => {
        const section = sections.value.find(s => s.name === name);
        if (section) {
            section.open = open;
            saveSectionsToStorage();
        }
    };

    const getSectionState = (name: string): boolean => {
        const section = sections.value.find(s => s.name === name);
        return section ? section.open : false;
    };

    const resetSections = () => {
        sections.value = [...DEFAULT_SECTIONS];
        saveSectionsToStorage();
    };

    loadSectionsFromStorage();

    return {
        sections,
        loadSectionsFromStorage,
        toggleSection,
        setSectionState,
        getSectionState,
        resetSections,
    };
});
